import React, { useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { Oval } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';
import EquipmentSearchBar from './EquipmentSearchBar';
import OperationalStatus from './OperationalStatus';
import ConnectedEquipmentView from './ConnectedEquipmentView';
import AssetOverview, { AssetOverviewHandle } from './AssetOverview';
import {
    IdashboardAnalyticsData,
    Ifacility,
    Iuser,
    IproductInfo
} from '../../models';
import { IinitialState } from '../../reducers';
import { msalFetch } from '../auth/Auth-Utils';
import API from '../../constants/apiEndpoints';
import { beginAjaxCall, endAjaxCall } from '../../actions/ajaxStatusActions';
import { runCodeDeficiencyReport } from '../../actions/manageReportActions';
import {
    setTableFilter,
    getProductInfo as getProductInfoAction
} from '../../actions/manageInventoryActions';
import { getFacility } from '../../actions/manageLocationActions';
import { measurementPointResultStatusTypesEnum } from '../../models-enums';
import { getProductInfo } from '../../reducers/productInfoReducer';
import { selectSelectedFacilityWithBuildings } from '../../reducers/facilitiesReducer';
import { applyStatusLabelOverrides } from '../../helpers/statusLabelHelper';
import { constants } from '../../constants/constants';

interface Iprops {
    user: Iuser;
    selectedFacilityID: string;
    selectedFacility: Ifacility;
    productInfo: IproductInfo;
    beginAjaxCall: typeof beginAjaxCall;
    endAjaxCall: typeof endAjaxCall;
    runCodeDeficiencyReport: typeof runCodeDeficiencyReport;
    setTableFilter: typeof setTableFilter;
    getFacility: typeof getFacility;
    getProductInfo: typeof getProductInfoAction;
}

const DashboardEquipment: React.FC<Iprops> = ({
    selectedFacilityID,
    selectedFacility,
    productInfo,
    beginAjaxCall: startAjaxCall,
    endAjaxCall: stopAjaxCall,
    runCodeDeficiencyReport: runReport,
    setTableFilter: setFilter,
    getFacility: fetchFacilityHierarchy,
    getProductInfo: loadProductInfo
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const assetOverviewRef = useRef<AssetOverviewHandle>(null);
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [selectedCategory, setSelectedCategory] = React.useState<string>(
        'All'
    );
    const [selectedStatus, setSelectedStatus] = React.useState<string>('All');
    const [selectedBuilding, setSelectedBuilding] = React.useState<string>(
        'All'
    );
    const [selectedFloor, setSelectedFloor] = React.useState<string>('All');
    const [selectedZone, setSelectedZone] = React.useState<string>('All');
    const [
        hideDecommissionedAssets,
        setHideDecommissionedAssets
    ] = React.useState<boolean>(false);

    // Ensure productInfo is loaded when component mounts
    React.useEffect(() => {
        if (
            !productInfo?.mainCategoryOptions ||
            productInfo.mainCategoryOptions.length === 0
        ) {
            loadProductInfo();
        }
        // eslint-disable-next-line
    }, []);

    const getDashboardAnalytics = async () => {
        if (!selectedFacilityID) {
            return null;
        }

        startAjaxCall();

        try {
            const response = await msalFetch(API.update.dashboard, {
                method: 'get',
                params: {
                    facilityID: selectedFacilityID,
                    search: searchTerm || undefined,
                    mainCategoryID:
                        selectedCategory !== 'All'
                            ? selectedCategory
                            : undefined,
                    status:
                        selectedStatus !== 'All' ? selectedStatus : undefined,
                    buildingID:
                        selectedBuilding !== 'All'
                            ? selectedBuilding
                            : undefined,
                    floorID:
                        selectedFloor !== 'All' ? selectedFloor : undefined,
                    locationID:
                        selectedZone !== 'All' ? selectedZone : undefined,
                    hideDecommissioned: hideDecommissionedAssets || undefined
                }
            });

            if (!response.data) {
                throw new Error('missing data');
            }
            return response.data as IdashboardAnalyticsData;
        } catch (error) {
            console.error(
                '[DashboardEquipment - getDashboardAnalytics]',
                error
            );
            return null;
        } finally {
            stopAjaxCall();
        }
    };

    const { data: dashboardAnalytics, isLoading, isFetching } = useQuery(
        [
            'equipment-analytics',
            selectedFacilityID,
            searchTerm,
            selectedCategory,
            selectedStatus,
            selectedBuilding,
            selectedFloor,
            selectedZone,
            hideDecommissionedAssets
        ],
        getDashboardAnalytics,
        {
            refetchOnWindowFocus: false,
            enabled: !!selectedFacilityID
        }
    );

    // Fetch facility hierarchy when facility changes
    React.useEffect(() => {
        if (selectedFacilityID) {
            fetchFacilityHierarchy(selectedFacilityID);
        }
    }, [selectedFacilityID, fetchFacilityHierarchy]);

    // Generate category options from productInfo (all available categories)
    const categoryOptions = useMemo(() => {
        if (
            !productInfo ||
            !productInfo.mainCategoryOptions ||
            !Array.isArray(productInfo.mainCategoryOptions)
        ) {
            return [{ value: 'All', label: 'Category: All' }];
        }

        // Convert productInfo options to the format needed for FilterDropdown
        const options = productInfo.mainCategoryOptions.map(option => ({
            value: option.value?.toString() || '',
            label: option.label || ''
        }));

        // Add "All" option at the beginning
        return [{ value: 'All', label: 'Category: All' }, ...options];
    }, [productInfo]);

    // Generate status options from dashboard analytics inspection data
    const statusOptions = useMemo(() => {
        if (!dashboardAnalytics || !dashboardAnalytics.inspection) {
            return [{ value: 'All', label: 'Status: All' }];
        }

        // Get the selected inspection category data (or ALL category)
        let selectedInspection = dashboardAnalytics.inspection.find(
            item => item.mainCategoryID === selectedCategory
        );

        if (!selectedInspection && selectedCategory === 'All') {
            selectedInspection = dashboardAnalytics.inspection.find(
                item => item.total > 0
            );
        }

        if (!selectedInspection || !selectedInspection.data) {
            return [{ value: 'All', label: 'Status: All' }];
        }

        // Extract unique statuses from the inspection data
        const statusSet = new Set<string>();
        selectedInspection.data.forEach((item: any) => {
            if (item.name) {
                // Convert API status names to lowercase (Pass -> pass, Fail -> fail, etc.)
                const statusName =
                    item.name.charAt(0).toLowerCase() + item.name.slice(1);
                statusSet.add(statusName);
            }
        });

        // Convert to option format with colors
        const options = Array.from(statusSet).map(statusName => {
            const translatedLabel = t(`dashboardAnalytics:${statusName}`);
            const displayLabel = applyStatusLabelOverrides(translatedLabel);
            const color =
                constants.installBaseStatusColorLookup[
                    translatedLabel as keyof typeof constants.installBaseStatusColorLookup
                ] || constants.colors.greyBlue;
            return {
                value: statusName,
                label: displayLabel,
                color
            };
        });

        // Add "All" option at the beginning
        return [{ value: 'All', label: 'Status: All' }, ...options];
    }, [dashboardAnalytics, selectedCategory, t]);

    // Generate building options from facility hierarchy
    const buildingOptions = useMemo(() => {
        if (!selectedFacility || !selectedFacility.buildings) {
            return [{ value: 'All', label: 'Bldg: All' }];
        }

        // Convert buildings to options format and sort by name
        const options = selectedFacility.buildings
            .map(building => ({
                value: building.id,
                label: building.name
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        // Add "All" option at the beginning
        return [{ value: 'All', label: 'Bldg: All' }, ...options];
    }, [selectedFacility]);

    // Generate floor options from facility hierarchy (filtered by selected building)
    const floorOptions = useMemo(() => {
        if (!selectedFacility || !selectedFacility.buildings) {
            return [{ value: 'All', label: 'Floor: No options' }];
        }

        // Only show floor options if a specific building is selected
        if (selectedBuilding === 'All') {
            return [{ value: 'All', label: 'Floor: No options' }];
        }

        // Find the selected building
        const building = selectedFacility.buildings.find(
            b => b.id === selectedBuilding
        );

        if (!building || !building.floors) {
            return [{ value: 'All', label: 'Floor: No options' }];
        }

        // Convert floors to options format and sort by name
        const options = building.floors
            .map(floor => ({
                value: floor.id,
                label: floor.name
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        // Add "All" option at the beginning
        return [{ value: 'All', label: 'Floor: All' }, ...options];
    }, [selectedFacility, selectedBuilding]);

    // Generate zone (location) options from facility hierarchy (filtered by selected building and floor)
    const zoneOptions = useMemo(() => {
        if (!selectedFacility || !selectedFacility.buildings) {
            return [{ value: 'All', label: 'Zone: No options' }];
        }

        // Only show zone options if a specific building and floor are selected
        if (selectedBuilding === 'All' || selectedFloor === 'All') {
            return [{ value: 'All', label: 'Zone: No options' }];
        }

        // Find the selected building
        const building = selectedFacility.buildings.find(
            b => b.id === selectedBuilding
        );

        if (!building || !building.floors) {
            return [{ value: 'All', label: 'Zone: No options' }];
        }

        // Find the selected floor
        const floor = building.floors.find(f => f.id === selectedFloor);

        if (!floor || !floor.locations) {
            return [{ value: 'All', label: 'Zone: No options' }];
        }

        // Convert locations to options format and sort by name
        const options = floor.locations
            .map(location => ({
                value: location.id,
                label: location.name
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        // Add "All" option at the beginning
        return [{ value: 'All', label: 'Zone: All' }, ...options];
    }, [selectedFacility, selectedBuilding, selectedFloor]);

    // Derive the selected category name from the ID for filtering equipment by name
    const selectedCategoryName = useMemo(() => {
        if (selectedCategory === 'All') {
            return 'All';
        }
        const category = productInfo.mainCategoryOptions.find(
            opt => opt.value === selectedCategory
        );
        return category ? category.label : 'All';
    }, [selectedCategory, productInfo.mainCategoryOptions]);

    const handleBackClick = () => {
        history.push('/dashboard');
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleCategoryChange = (category: string) => {
        setSelectedCategory(category);
    };

    const handleStatusChange = (status: string) => {
        setSelectedStatus(status);
    };

    const handleBuildingChange = (building: string) => {
        setSelectedBuilding(building);
        // Reset dependent filters
        setSelectedFloor('All');
        setSelectedZone('All');
    };

    const handleFloorChange = (floor: string) => {
        setSelectedFloor(floor);
        // Reset dependent filter
        setSelectedZone('All');
    };

    const handleZoneChange = (zone: string) => {
        setSelectedZone(zone);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedCategory('All');
        setSelectedStatus('All');
        setSelectedBuilding('All');
        setSelectedFloor('All');
        setSelectedZone('All');
    };

    // Filter inspection data based on selected status and category for OperationalStatus component
    const filteredInspectionData = useMemo(() => {
        if (!dashboardAnalytics?.inspection) {
            return [];
        }

        let filteredInspection = dashboardAnalytics.inspection;

        // Filter by category first
        if (selectedCategory !== 'All') {
            filteredInspection = filteredInspection.filter(
                inspectionCategory =>
                    inspectionCategory.mainCategoryID === selectedCategory
            );
        }

        // Then filter by status
        if (selectedStatus !== 'All' && selectedStatus !== '') {
            filteredInspection = filteredInspection
                .map(inspectionCategory => {
                    // Filter the data array within each inspection category
                    const filteredData = inspectionCategory.data.filter(
                        (item: any) => {
                            const statusName = item.name
                                ? item.name.charAt(0).toLowerCase() +
                                  item.name.slice(1)
                                : '';
                            return statusName === selectedStatus;
                        }
                    );

                    // Convert percentage to actual count
                    // Original y values are percentages, we need to calculate the actual count
                    const actualCount = filteredData.reduce(
                        (sum: number, item: any) => {
                            // y is a percentage, so (y / 100) * originalTotal gives actual count
                            const count = Math.round(
                                (item.y / 100) * inspectionCategory.total
                            );
                            return sum + count;
                        },
                        0
                    );

                    // For filtered data, set y to 100 (represents 100% of filtered results)
                    // and total to the actual count
                    const adjustedData = filteredData.map(item => ({
                        ...item,
                        y: 100
                    }));

                    return {
                        ...inspectionCategory,
                        data: adjustedData,
                        total: actualCount
                    };
                })
                .filter(inspectionCategory => inspectionCategory.total > 0);
        }

        return filteredInspection;
    }, [dashboardAnalytics, selectedStatus, selectedCategory]);

    const handleStatusSegmentClick = (statusEnum: number) => {
        // Map numeric status enum to string status name used in inspectionDetail
        const statusEnumToStringMap: { [key: number]: string } = {
            [measurementPointResultStatusTypesEnum.resultStatusPass]: 'pass',
            [measurementPointResultStatusTypesEnum.resultStatusFail]: 'fail',
            [measurementPointResultStatusTypesEnum.resultStatusRepaired]:
                'repaired',
            [measurementPointResultStatusTypesEnum.resultStatusNotTested]:
                'notTested',
            [measurementPointResultStatusTypesEnum.resultStatusCannotComplete]:
                'cannotComplete'
        };

        const statusString = statusEnumToStringMap[statusEnum];

        // Call the AssetDetailsView ref to scroll and expand the status
        if (assetOverviewRef.current && statusString) {
            assetOverviewRef.current.scrollToAndExpandStatus(statusString);
        }
    };

    // Check if all three components have no data
    const hasOperationalStatusData = useMemo(() => {
        if (!dashboardAnalytics) return false;
        const totalInspections =
            filteredInspectionData.reduce(
                (sum, category) => sum + (category.total || 0),
                0
            ) || 0;
        const totalPMP = dashboardAnalytics?.pmp?.totalWorkOrders || 0;
        return totalInspections > 0 || totalPMP > 0;
    }, [filteredInspectionData, dashboardAnalytics]);

    const hasAssetOverviewData = useMemo(() => {
        if (!dashboardAnalytics) return false;
        return (
            dashboardAnalytics.inspectionDetail &&
            dashboardAnalytics.inspectionDetail.length > 0
        );
    }, [dashboardAnalytics]);

    // For a complete "no data" state, check if dashboardAnalytics exists and has at least one section with data
    const hasAnyData = Boolean(
        dashboardAnalytics &&
            (hasOperationalStatusData ||
                hasAssetOverviewData ||
                (dashboardAnalytics.smartlinks?.length ?? 0) > 0)
    );

    return (
        <div className="dashboard-equipment">
            <EquipmentSearchBar
                onBackClick={handleBackClick}
                onSearchChange={handleSearchChange}
                categoryOptions={categoryOptions}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
                statusOptions={statusOptions}
                selectedStatus={selectedStatus}
                onStatusChange={handleStatusChange}
                buildingOptions={buildingOptions}
                selectedBuilding={selectedBuilding}
                onBuildingChange={handleBuildingChange}
                floorOptions={floorOptions}
                selectedFloor={selectedFloor}
                onFloorChange={handleFloorChange}
                zoneOptions={zoneOptions}
                selectedZone={selectedZone}
                onZoneChange={handleZoneChange}
                onClearFilters={handleClearFilters}
                loading={isLoading}
                hideDecommissionedAssets={hideDecommissionedAssets}
                onHideAssetsToggle={setHideDecommissionedAssets}
            />
            <div className="equipment-content">
                {isLoading || isFetching ? (
                    <div className="loading-container">
                        <Oval
                            color="#ff9738"
                            secondaryColor="#ffcd9e"
                            height={60}
                            width={60}
                        />
                    </div>
                ) : !hasAnyData ? (
                    <div className="no-data-container">
                        <p className="no-data-message">
                            No data available for this facility.
                        </p>
                    </div>
                ) : (
                    <>
                        <OperationalStatus
                            inspection={
                                dashboardAnalytics ? filteredInspectionData : []
                            }
                            inspectionDates={
                                dashboardAnalytics?.inspectionDates || null
                            }
                            pmp={
                                dashboardAnalytics?.pmp || {
                                    totalWorkOrders: 0,
                                    data: { open: [], closed: [] }
                                }
                            }
                            selectedFacility={selectedFacility}
                            selectedCategory={selectedCategory}
                            runCodeDeficiencyReport={runReport}
                            setTableFilter={setFilter}
                            onStatusSegmentClick={handleStatusSegmentClick}
                            t={t}
                        />
                        <ConnectedEquipmentView
                            t={t}
                            selectedFacilityID={selectedFacilityID}
                            selectedCategory={selectedCategoryName}
                            searchTerm={searchTerm}
                            selectedStatus={selectedStatus}
                            selectedBuilding={selectedBuilding}
                            selectedFloor={selectedFloor}
                            selectedZone={selectedZone}
                            hideDecommissionedAssets={hideDecommissionedAssets}
                        />
                        <AssetOverview
                            ref={assetOverviewRef}
                            t={t}
                            data={dashboardAnalytics || null}
                            isLoading={isLoading}
                            searchTerm={searchTerm}
                            selectedStatus={selectedStatus}
                            selectedLocation="All"
                            selectedCategory={selectedCategory}
                            setTableFilter={setFilter}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = (state: IinitialState) => ({
    user: state.user,
    selectedFacilityID: state.selectedFacilityID,
    selectedFacility: selectSelectedFacilityWithBuildings(state),
    productInfo: getProductInfo(state)
});

export default connect(mapStateToProps, {
    beginAjaxCall,
    endAjaxCall,
    runCodeDeficiencyReport,
    setTableFilter,
    getFacility,
    getProductInfo: getProductInfoAction
})(DashboardEquipment);
